import React, { useMemo } from 'react';
import { Alert } from 'antd';
import type { RouteOperationalProcessesPayload } from '../../operationalDomain/processes/operationalProcessTypes';
import { buildOperationalProcessSummary } from '../../operationalDomain/processes/buildOperationalProcessSummary';
import { PROCESS_TYPE_LABELS } from '../../operationalDomain/processes/processTypes';

export type ExecutionProcessPanelProps = {
  payload: RouteOperationalProcessesPayload | null | undefined;
  loading?: boolean;
};

const ExecutionProcessPanel: React.FC<ExecutionProcessPanelProps> = ({ payload, loading }) => {
  const lines = useMemo(
    () => buildOperationalProcessSummary(payload?.processes ?? []),
    [payload?.processes]
  );

  const primaryOverdue = useMemo(() => {
    const overdue = payload?.overdueProcesses?.[0] ?? payload?.processes?.find((p) => p.ownership?.overdue.isOverdue);
    if (!overdue?.ownership?.overdue.isOverdue) return null;
    return overdue;
  }, [payload]);

  if (loading) {
    return (
      <section className="execution-workspace__panel execution-workspace__panel--muted">
        <div className="execution-workspace__panel-title">Операционные процессы</div>
        <div className="execution-workspace__muted">Загрузка…</div>
      </section>
    );
  }

  if (!lines.length && !primaryOverdue) return null;

  return (
    <section className="execution-workspace__panel">
      <div className="execution-workspace__panel-title">Операционные процессы</div>
      {primaryOverdue ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message="Просрочен процесс"
          description={
            <div className="execution-process-overdue">
              <div>
                {lines.find((l) => l.overdue)?.label ??
                  PROCESS_TYPE_LABELS[primaryOverdue.type] ??
                  primaryOverdue.type}
              </div>
              {primaryOverdue.ownership?.overdue.overdueOwnerDisplayName ? (
                <div>
                  Ответственный: {primaryOverdue.ownership.overdue.overdueOwnerDisplayName}
                </div>
              ) : null}
              {primaryOverdue.ownership?.overdue.durationLabel ? (
                <div>Просрочка: {primaryOverdue.ownership.overdue.durationLabel}</div>
              ) : null}
              {primaryOverdue.ownership?.overdue.escalationCandidateDisplayName ? (
                <div className="execution-workspace__muted">
                  Эскалация: {primaryOverdue.ownership.overdue.escalationCandidateDisplayName}
                </div>
              ) : null}
            </div>
          }
        />
      ) : null}
      {lines.length > 0 ? (
        <ul className="execution-process-summary">
          {lines.map((line) => (
            <li
              key={line.id}
              className={`execution-process-summary__item execution-process-summary__item--${line.tone}${
                line.overdue ? ' execution-process-summary__item--overdue' : ''
              }`}
            >
              <div className="execution-process-summary__main">
                <span className="execution-process-summary__label">{line.label}</span>
                <span className="execution-process-summary__status">— {line.statusLabel}</span>
              </div>
              {line.responsibleDisplayName ? (
                <div className="execution-process-summary__meta">
                  Ответственный: {line.responsibleDisplayName}
                </div>
              ) : null}
              {line.slaExpectedAt && !line.overdue ? (
                <div className="execution-process-summary__meta">SLA: {line.slaExpectedAt}</div>
              ) : null}
              {line.overdue && line.overdueDurationLabel ? (
                <div className="execution-process-summary__meta execution-process-summary__meta--overdue">
                  Просрочено: {line.overdueDurationLabel}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};

export default ExecutionProcessPanel;
